import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/Modal";
import Button from "@/components/ui/Button";
import { useToast } from "@/components";
import type { ChooseSectorsDialogProps } from "../../../types";

/**
 * Dialog component for selecting sectors for irrigation plan steps.
 * Provides search functionality and multi-select interface.
 */
function ChooseSectorsDialog({
  isOpen,
  onClose,
  sectors,
  selectedSectorIds,
  onSectorsChange,
  mirrorTargetSectors,
}: ChooseSectorsDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedSectorIds);
  const { showError } = useToast();

  // Build a map of mirror target sector IDs → source sector name
  const mirrorTargetMap = useMemo(() => {
    if (!mirrorTargetSectors) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const { sectorId, sourceSectorName } of mirrorTargetSectors) {
      map.set(sectorId, sourceSectorName);
    }
    return map;
  }, [mirrorTargetSectors]);

  // Sync localSelectedIds with selectedSectorIds whenever the prop changes or dialog opens
  useEffect(() => {
    setLocalSelectedIds(selectedSectorIds);
  }, [selectedSectorIds]);

  // Additional sync when dialog opens to ensure state is correct
  useEffect(() => {
    // console.log("ChooseSectorsDialog", { isOpen, selectedSectorIds });
    if (isOpen) {
      setLocalSelectedIds(selectedSectorIds);
    }
  }, [isOpen, selectedSectorIds]);

  // Filter sectors based on search term
  const filteredSectors = sectors.filter(
    (sector) =>
      sector.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sector.description && sector.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSectorToggle = (sectorId: string) => {
    setLocalSelectedIds((prev) =>
      prev.includes(sectorId) ? prev.filter((id) => id !== sectorId) : [...prev, sectorId]
    );
  };

  const handleSave = async () => {
    try {
      await onSectorsChange(localSelectedIds);
      onClose();
    } catch (error) {
      console.error("Erro ao atualizar setores selecionados:", error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Erro ao salvar a seleção de setores.";
      showError({ message });
      onClose();
    }
  };

  const handleCancel = () => {
    setLocalSelectedIds(selectedSectorIds);
    onClose();
  };

  // Calculate select all state for filtered sectors (exclude mirror targets)
  const selectableSectorIds = filteredSectors
    .map((sector) => sector.id)
    .filter((id) => !mirrorTargetMap.has(id));
  const allFilteredSelected =
    selectableSectorIds.length > 0 && selectableSectorIds.every((id) => localSelectedIds.includes(id));
  const someFilteredSelected = selectableSectorIds.some((id) => localSelectedIds.includes(id));
  const isIndeterminate = someFilteredSelected && !allFilteredSelected;

  // Handle select all toggle for filtered sectors (exclude mirror targets)
  const handleSelectAllToggle = () => {
    if (allFilteredSelected) {
      // Unselect all selectable filtered sectors
      setLocalSelectedIds((prev) => prev.filter((id) => !selectableSectorIds.includes(id)));
    } else {
      // Select all selectable filtered sectors
      setLocalSelectedIds((prev) => {
        const newIds = [...prev];
        selectableSectorIds.forEach((id) => {
          if (!newIds.includes(id)) {
            newIds.push(id);
          }
        });
        return newIds;
      });
    }
  };

  return (
    <Modal title="Escolher Setores" isOpen={isOpen} onClose={handleCancel} showCloseButton={true}>
      <div className="space-y-4">
        {/* Search Box */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar setores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        {/* Select All Header */}
        {filteredSectors.length > 0 && (
          <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
            <div className="relative">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                ref={(input) => {
                  if (input) input.indeterminate = isIndeterminate;
                }}
                onChange={handleSelectAllToggle}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
            </div>
            <span className="text-sm font-medium text-gray-700">
              {allFilteredSelected
                ? `Todos selecionados (${selectableSectorIds.filter((id) => localSelectedIds.includes(id)).length})`
                : someFilteredSelected
                  ? `${selectableSectorIds.filter((id) => localSelectedIds.includes(id)).length} de ${selectableSectorIds.length} selecionados`
                  : "Selecionar todos"}
            </span>
          </div>
        )}

        {/* Sectors List */}
        <div className=" space-y-1">
          {filteredSectors.map((sector) => {
            const isMirrorTarget = mirrorTargetMap.has(sector.id);
            const sourceSectorName = mirrorTargetMap.get(sector.id);
            const isSelected = localSelectedIds.includes(sector.id);

            return (
              <div
                key={sector.id}
                className={`flex items-center gap-2 p-2 border border-gray-200 rounded-lg ${
                  isMirrorTarget
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-gray-50 cursor-pointer"
                }`}
                onClick={() => !isMirrorTarget && handleSectorToggle(sector.id)}
              >
                <input
                  type="checkbox"
                  checked={!isMirrorTarget && isSelected}
                  readOnly
                  className={`h-4 w-4 rounded pointer-events-none ${
                    isMirrorTarget
                      ? "text-gray-300 border-gray-200"
                      : "text-green-600 focus:ring-green-500 border-gray-300"
                  }`}
                />
                <div className="flex-1">
                  <div className={`font-medium ${isMirrorTarget ? "text-gray-400" : "text-gray-900"}`}>
                    {sector.name}
                    {isMirrorTarget && <span> (espelho de {sourceSectorName})</span>}
                  </div>
                  {!isMirrorTarget && sector.description && (
                    <div className="text-sm text-gray-500">{sector.description}</div>
                  )}
                </div>
              </div>
            );
          })}
          {filteredSectors.length === 0 && (
            <div className="text-center py-8 text-gray-500">Nenhum setor encontrado</div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button onClick={handleCancel} variant="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} variant="primary">
            Salvar ({localSelectedIds.length} selecionados)
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ChooseSectorsDialog;
